import { useStrings } from "@1js/localization-components";
import {
  Menu,
  MenuDivider,
  MenuItem,
  MenuItemCheckbox,
  MenuList,
  MenuPopover,
  MenuTrigger,
  type MenuProps,
} from "@fluentui/react-components";
import * as React from "react";
import type { Appearance } from "../../data";
import { ResetFilterLabel } from "../../sharedStrings";
import { useStyles } from "../PromptFilter.styles";
import {
  CLEAR_FILTER_TOKEN,
  FILTER_CONCATENATE_TOKEN,
  type FilterOption,
} from "../types";
import { usePromptFilterLocalizations } from "../usePromptFilterStrings";
export type SubMenuFilterProps = {
  filterOptions: FilterOption[];
  triggerDisplayLabel: string;
  groupDisplayLabel: string;
  selectedValues: string[] | undefined;
  onChangeChecked: MenuProps["onCheckedValueChange"];
  checkedValues: Record<string, string[]>;
  isDisabled?: boolean;
  appearance?: Appearance;
};

export const SubMenuFilter: React.FC<SubMenuFilterProps> = ({
  filterOptions,
  groupDisplayLabel,
  triggerDisplayLabel,
  selectedValues,
  onChangeChecked,
  checkedValues,
  isDisabled,
  appearance,
}) => {
  const { facetValueToLabel, groupValueToLabel } =
    usePromptFilterLocalizations();
  const styles = useStyles(appearance)();
  const localizations = useStrings({
    ResetFilterLabel,
  });

  const selectAllChecked =
    Object.keys(selectedValues || {}).length < 1
      ? {
          [`${CLEAR_FILTER_TOKEN}${FILTER_CONCATENATE_TOKEN}${groupDisplayLabel}${FILTER_CONCATENATE_TOKEN}${triggerDisplayLabel}`]:
            [
              `${CLEAR_FILTER_TOKEN}${FILTER_CONCATENATE_TOKEN}${groupDisplayLabel}${FILTER_CONCATENATE_TOKEN}${triggerDisplayLabel}`,
            ],
        }
      : {};

  return (
    <Menu>
      <MenuTrigger disableButtonEnhancement>
        <MenuItem
          data-testid={`${groupDisplayLabel}-${triggerDisplayLabel}-submenu`}
          disabled={isDisabled}
        >
          {groupValueToLabel(triggerDisplayLabel)}
        </MenuItem>
      </MenuTrigger>
      <MenuPopover className={styles.menuPopover}>
        <MenuList
          onCheckedValueChange={onChangeChecked}
          checkedValues={{ ...checkedValues, ...selectAllChecked }}
        >
          <MenuItemCheckbox
            name={`${CLEAR_FILTER_TOKEN}${FILTER_CONCATENATE_TOKEN}${groupDisplayLabel}${FILTER_CONCATENATE_TOKEN}${triggerDisplayLabel}`}
            value={`${CLEAR_FILTER_TOKEN}${FILTER_CONCATENATE_TOKEN}${groupDisplayLabel}${FILTER_CONCATENATE_TOKEN}${triggerDisplayLabel}`}
            key={CLEAR_FILTER_TOKEN}
            aria-label={`selectall-menuitemcheckbox`}
            aria-checked={selectedValues?.includes(
              `${CLEAR_FILTER_TOKEN}${FILTER_CONCATENATE_TOKEN}${groupDisplayLabel}${FILTER_CONCATENATE_TOKEN}${triggerDisplayLabel}`
            )}
            secondaryContent={localizations.ResetFilterLabel()}
          >
            {facetValueToLabel(CLEAR_FILTER_TOKEN)}
          </MenuItemCheckbox>
          <MenuDivider />
          {filterOptions.map((filterOption) => {
            return (
              <MenuItemCheckbox
                name={
                  filterOption.displayLabel ||
                  facetValueToLabel(filterOption.value)
                }
                value={`${filterOption.value}${FILTER_CONCATENATE_TOKEN}${groupDisplayLabel}${FILTER_CONCATENATE_TOKEN}${triggerDisplayLabel}`}
                key={filterOption.value}
                aria-label={`${filterOption.displayLabel || facetValueToLabel(filterOption.value)}-menuitemcheckbox`}
                aria-checked={selectedValues?.includes(filterOption.value)}
                className={styles.menuItemLineWrap}
                data-testid={`${groupDisplayLabel}-filter-submenuitemcheckbox`}
              >
                {filterOption.displayLabel ||
                  facetValueToLabel(filterOption.value)}
              </MenuItemCheckbox>
            );
          })}
        </MenuList>
      </MenuPopover>
    </Menu>
  );
};
